import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pencil, Trash2, Search, X, Tag as TagIcon, MessageCircle, User } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, StatusBadge, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CLIENT_TYPES, BRAZIL_STATES } from '../utils/constants';
import { formatDocument, formatPhone } from '../utils/format';

const emptyForm = {
  person_type: 'PF',
  name: '',
  trade_name: '',
  corporate_name: '',
  cpf: '',
  cnpj: '',
  ie: '',
  im: '',
  client_type: 'Turismo',
  contacts: {
    first_name: '',
    last_name: '',
    phone: '',
    whatsapp: '',
    email: '',
    comm_first_name: '',
    comm_last_name: '',
    comm_phone: '',
    comm_whatsapp: '',
    comm_email: '',
  },
  address: { zip_code: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' },
  commercial: { category_id: '', origin: '', responsible_user_name: '', status: 'active', notes: '' },
  tags: [],
};

const openWhatsApp = (phone) => {
  if (!phone) {
    toast.error('Informe um número de WhatsApp primeiro');
    return;
  }
  const clean = String(phone).replace(/\D/g, '');
  if (!clean) {
    toast.error('Número de WhatsApp inválido');
    return;
  }
  const number = (clean.length === 10 || clean.length === 11) && !clean.startsWith('55')
    ? `55${clean}`
    : clean;
  window.open(`https://wa.me/${number}`, '_blank', 'noopener,noreferrer');
};

export default function Clients() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [personType, setPersonType] = useState('all');
  const [clientType, setClientType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [tagsList, setTagsList] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (personType !== 'all') params.person_type = personType;
      if (clientType !== 'all') params.client_type = clientType;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const { data } = await api.get('/clients', { params });
      setItems(data.items);
      setTotal(data.total);
    } catch {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadAux = async () => {
    try {
      const [c, t] = await Promise.all([
        api.get('/categories', { params: { type: 'client', limit: 100 } }),
        api.get('/tags', { params: { limit: 100 } }),
      ]);
      setCategories(c.data.items || []);
      setTagsList(t.data.items || []);
    } catch {}
  };

  useEffect(() => { loadAux(); }, []);
  useEffect(() => { load(); }, [page, personType, clientType, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...emptyForm,
      ...c,
      contacts: { ...emptyForm.contacts, ...(c.contacts || {}) },
      address: { ...emptyForm.address, ...(c.address || {}) },
      commercial: { ...emptyForm.commercial, ...(c.commercial || {}) },
      tags: c.tags || [],
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Ensure required doc based on person_type
      if (payload.person_type === 'PF' && !payload.cpf) payload.cpf = '';
      if (payload.person_type === 'PJ' && !payload.cnpj) payload.cnpj = '';
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success('Cliente atualizado');
      } else {
        await api.post('/clients', payload);
        toast.success('Cliente cadastrado');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      let msg = 'Erro ao salvar';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = 'Verifique os campos obrigatórios.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/clients/${deleteId}`);
      toast.success('Cliente excluído');
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const clearFilters = () => { setPersonType('all'); setClientType('all'); setStatusFilter('all'); setSearch(''); };
  const toggleTag = (tagId) => {
    setForm((f) => ({ ...f, tags: f.tags.includes(tagId) ? f.tags.filter(t => t !== tagId) : [...f.tags, tagId] }));
  };

  const docFor = (c) => c.person_type === 'PF' ? (c.cpf || '') : (c.cnpj || '');

  return (
    <div>
      <PageHeader title="Clientes" subtitle="Gerencie os clientes da empresa" onAction={openCreate} actionLabel="Novo Cliente" />

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, documento, email..." className="pl-9" />
          </div>
          <Select value={personType} onValueChange={setPersonType}>
            <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Tipo Pessoa" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="PF">Pessoa Física</SelectItem>
              <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientType} onValueChange={setClientType}>
            <SelectTrigger className="w-full md:w-44"><SelectValue placeholder="Tipo Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              {CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativo</SelectItem>
              <SelectItem value="inactive">Inativo</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="lead">Lead</SelectItem>
            </SelectContent>
          </Select>
          {(search || personType !== 'all' || clientType !== 'all' || statusFilter !== 'all') && (
            <Button variant="outline" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Limpar</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium">Documento</th>
                <th className="text-left px-4 py-3 font-medium">Contato</th>
                <th className="text-left px-4 py-3 font-medium">Cidade/UF</th>
                <th className="text-left px-4 py-3 font-medium">Tipo</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={7} className="p-4"><TableSkeleton rows={6} cols={7} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon={Search} title="Nenhum cliente encontrado" description="Ajuste os filtros ou cadastre um novo cliente." /></td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="tr-hover">
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-neutral-500">{c.trade_name || c.contacts?.email || '-'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs">{formatDocument(docFor(c), c.person_type)}</span>
                    <div className="text-[10px] text-neutral-500 mt-0.5">{c.person_type}</div>
                  </td>
                  <td className="px-4 py-3">
                    {([c.contacts?.first_name, c.contacts?.last_name].filter(Boolean).join(' ')) && (
                      <div className="font-medium text-xs text-neutral-800 dark:text-neutral-200">
                        {[c.contacts?.first_name, c.contacts?.last_name].filter(Boolean).join(' ')}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
                      <span>{formatPhone(c.contacts?.whatsapp || c.contacts?.phone) || '-'}</span>
                      {c.contacts?.whatsapp && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openWhatsApp(c.contacts.whatsapp); }}
                          className="inline-flex items-center justify-center p-0.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded transition-colors"
                          title={`Conversar no WhatsApp (${c.contacts.whatsapp})`}
                        >
                          <MessageCircle className="h-3.5 w-3.5 fill-emerald-100 dark:fill-emerald-950" />
                        </button>
                      )}
                    </div>
                    {c.contacts?.email && (
                      <div className="text-[11px] text-neutral-500 truncate max-w-[190px]">{c.contacts.email}</div>
                    )}
                    {([c.contacts?.comm_first_name, c.contacts?.comm_last_name].filter(Boolean).join(' ') || c.contacts?.comm_whatsapp || c.contacts?.comm_phone) && (
                      <div className="mt-1 pt-1 border-t border-neutral-100 dark:border-neutral-800 text-[11px] text-neutral-500 flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] font-semibold text-neutral-400 uppercase">Comunicação:</span>
                        <span className="truncate max-w-[110px]">
                          {[c.contacts?.comm_first_name, c.contacts?.comm_last_name].filter(Boolean).join(' ') || formatPhone(c.contacts?.comm_whatsapp || c.contacts?.comm_phone)}
                        </span>
                        {c.contacts?.comm_whatsapp && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); openWhatsApp(c.contacts.comm_whatsapp); }}
                            className="inline-flex items-center justify-center p-0.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 rounded transition-colors"
                            title={`WhatsApp Comunicação (${c.contacts.comm_whatsapp})`}
                          >
                            <MessageCircle className="h-3 w-3 fill-emerald-100 dark:fill-emerald-950" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{c.address?.city ? `${c.address.city}/${c.address?.state || '-'}` : '-'}</td>
                  <td className="px-4 py-3">{c.client_type || '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={(c.commercial?.status === 'active' || c.status === 'active') ? 'Ativo' : 'Inativo'} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            <DialogDescription>Preencha os dados do cliente organizados por categoria.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Tabs defaultValue="geral" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="geral">Geral</TabsTrigger>
                <TabsTrigger value="contato">Contato</TabsTrigger>
                <TabsTrigger value="endereco">Endereço</TabsTrigger>
                <TabsTrigger value="comercial">Comercial</TabsTrigger>
              </TabsList>

              <TabsContent value="geral" className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1.5">
                  <Label>Tipo Pessoa *</Label>
                  <Select value={form.person_type} onValueChange={(v) => setForm({ ...form, person_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Tipo Cliente</Label>
                  <Select value={form.client_type} onValueChange={(v) => setForm({ ...form, client_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>{form.person_type === 'PF' ? 'Nome completo *' : 'Razão Social *'}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                {form.person_type === 'PJ' && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Nome Fantasia</Label>
                      <Input value={form.trade_name || ''} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>CNPJ</Label>
                      <Input value={form.cnpj || ''} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Inscrição Estadual</Label>
                      <Input value={form.ie || ''} onChange={(e) => setForm({ ...form, ie: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Inscrição Municipal</Label>
                      <Input value={form.im || ''} onChange={(e) => setForm({ ...form, im: e.target.value })} />
                    </div>
                  </>
                )}
                {form.person_type === 'PF' && (
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>CPF</Label>
                    <Input value={form.cpf || ''} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="contato" className="space-y-4 pt-3">
                {/* Contato Principal */}
                <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-neutral-200 dark:border-neutral-800">
                    <div className="h-7 w-7 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-xs">
                      <User className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Contato Principal</h4>
                      <p className="text-xs text-neutral-500">Contato titular ou responsável direto pelo cliente</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Nome</Label>
                      <Input
                        placeholder="Nome"
                        value={form.contacts.first_name || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, first_name: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Sobrenome</Label>
                      <Input
                        placeholder="Sobrenome"
                        value={form.contacts.last_name || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, last_name: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Telefone</Label>
                      <Input
                        placeholder="(00) 0000-0000"
                        value={form.contacts.phone || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, phone: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>WhatsApp</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="(00) 90000-0000"
                          value={form.contacts.whatsapp || ''}
                          onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, whatsapp: e.target.value } })}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!form.contacts.whatsapp}
                          onClick={() => openWhatsApp(form.contacts.whatsapp)}
                          className="shrink-0 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 disabled:opacity-40"
                          title="Abrir conversa no WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4 mr-1 text-emerald-600 dark:text-emerald-400" />
                          WhatsApp
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        placeholder="email@cliente.com"
                        value={form.contacts.email || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, email: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>

                {/* Contato de Comunicação */}
                <div className="p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-neutral-200 dark:border-neutral-800">
                    <div className="h-7 w-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-xs">
                      <MessageCircle className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Contato de Comunicação</h4>
                      <p className="text-xs text-neutral-500">Canal direto para avisos, operações e comunicação diária</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Nome</Label>
                      <Input
                        placeholder="Nome"
                        value={form.contacts.comm_first_name || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, comm_first_name: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Sobrenome</Label>
                      <Input
                        placeholder="Sobrenome"
                        value={form.contacts.comm_last_name || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, comm_last_name: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Telefone</Label>
                      <Input
                        placeholder="(00) 0000-0000"
                        value={form.contacts.comm_phone || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, comm_phone: e.target.value } })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>WhatsApp</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="(00) 90000-0000"
                          value={form.contacts.comm_whatsapp || ''}
                          onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, comm_whatsapp: e.target.value } })}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={!form.contacts.comm_whatsapp}
                          onClick={() => openWhatsApp(form.contacts.comm_whatsapp)}
                          className="shrink-0 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 disabled:opacity-40"
                          title="Abrir conversa no WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4 mr-1 text-emerald-600 dark:text-emerald-400" />
                          WhatsApp
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1.5 md:col-span-2">
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        placeholder="comunicacao@cliente.com"
                        value={form.contacts.comm_email || ''}
                        onChange={(e) => setForm({ ...form, contacts: { ...form.contacts, comm_email: e.target.value } })}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="endereco" className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                <div className="space-y-1.5">
                  <Label>CEP</Label>
                  <Input value={form.address.zip_code} onChange={(e) => setForm({ ...form, address: { ...form.address, zip_code: e.target.value } })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Rua</Label>
                  <Input value={form.address.street} onChange={(e) => setForm({ ...form, address: { ...form.address, street: e.target.value } })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Número</Label>
                  <Input value={form.address.number} onChange={(e) => setForm({ ...form, address: { ...form.address, number: e.target.value } })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Bairro</Label>
                  <Input value={form.address.neighborhood} onChange={(e) => setForm({ ...form, address: { ...form.address, neighborhood: e.target.value } })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Cidade</Label>
                  <Input value={form.address.city} onChange={(e) => setForm({ ...form, address: { ...form.address, city: e.target.value } })} />
                </div>
                <div className="space-y-1.5">
                  <Label>UF</Label>
                  <Select value={form.address.state} onValueChange={(v) => setForm({ ...form, address: { ...form.address, state: v } })}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{BRAZIL_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="comercial" className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1.5">
                  <Label>Categoria</Label>
                  <Select value={form.commercial.category_id || 'none'} onValueChange={(v) => setForm({ ...form, commercial: { ...form.commercial, category_id: v === 'none' ? '' : v } })}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Origem</Label>
                  <Input value={form.commercial.origin} onChange={(e) => setForm({ ...form, commercial: { ...form.commercial, origin: e.target.value } })} placeholder="Site, Indicação, etc." />
                </div>
                <div className="space-y-1.5">
                  <Label>Responsável</Label>
                  <Input value={form.commercial.responsible_user_name} onChange={(e) => setForm({ ...form, commercial: { ...form.commercial, responsible_user_name: e.target.value } })} placeholder="Nome do responsável" />
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.commercial.status} onValueChange={(v) => setForm({ ...form, commercial: { ...form.commercial, status: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label>Observações</Label>
                  <Textarea rows={3} value={form.commercial.notes} onChange={(e) => setForm({ ...form, commercial: { ...form.commercial, notes: e.target.value } })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="flex items-center gap-1.5"><TagIcon className="h-3.5 w-3.5" /> Tags</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {tagsList.length === 0 && <p className="text-xs text-neutral-500">Nenhuma tag disponível.</p>}
                    {tagsList.map(t => {
                      const active = form.tags.includes(t.id);
                      return (
                        <button key={t.id} type="button" onClick={() => toggleTag(t.id)}
                          className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${active ? 'text-white' : 'text-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                          style={active ? { background: t.color || '#C52429', borderColor: t.color || '#C52429' } : {}}>
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>O registro será marcado como excluído (soft delete). Pode ser restaurado depois.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
